/**
 * Helper JavaScript pour gérer le mode responsive/mobile
 */

export interface ResponsiveBreakpoint {
  maxWidth: number;
  name: string;
}

export const MOBILE_BREAKPOINT = 600;

/**
 * Détecte si on est en mode mobile
 */
export function isMobile(): boolean {
  if (typeof window === "undefined") return false;
  return window.innerWidth <= MOBILE_BREAKPOINT;
}

/**
 * Écoute les changements de taille de fenêtre et appelle un callback
 */
export function onResize(
  callback: (isMobile: boolean) => void,
  options?: { immediate?: boolean },
): () => void {
  if (typeof window === "undefined") {
    return () => {};
  }

  let lastIsMobile = isMobile();

  const handleResize = () => {
    const currentIsMobile = isMobile();
    if (currentIsMobile !== lastIsMobile) {
      lastIsMobile = currentIsMobile;
      callback(currentIsMobile);
    }
  };

  if (options?.immediate) {
    callback(lastIsMobile);
  }

  window.addEventListener("resize", handleResize);
  window.addEventListener("orientationchange", handleResize);

  return () => {
    window.removeEventListener("resize", handleResize);
    window.removeEventListener("orientationchange", handleResize);
  };
}

/**
 * Crée un MediaQueryList pour écouter les changements de breakpoint
 */
export function createMediaQuery(maxWidth: number): MediaQueryList | null {
  if (typeof window === "undefined" || !window.matchMedia) {
    return null;
  }

  return window.matchMedia(`(max-width: ${maxWidth}px)`);
}

/**
 * Écoute les changements d'un MediaQueryList
 */
export function onMediaQueryChange(
  mql: MediaQueryList | null,
  callback: (matches: boolean) => void,
  options?: { immediate?: boolean },
): () => void {
  if (!mql) {
    return () => {};
  }

  if (options?.immediate) {
    callback(mql.matches);
  }

  // Support moderne et ancien navigateurs
  if (mql.addEventListener) {
    mql.addEventListener("change", (e) => callback(e.matches));
    return () => {
      mql.removeEventListener("change", (e) => callback(e.matches));
    };
  } else {
    // Fallback pour anciens navigateurs
    mql.addListener((mql) => callback(mql.matches));
    return () => {
      mql.removeListener((mql) => callback(mql.matches));
    };
  }
}
